import type { Locale } from "@/i18n/languages";
import type { LabelName } from "./labels";
import {
  US_LABELS,
  CREDIT_NOTE_LABEL_NAMES,
  CREDIT_NOTE_LABEL_PRESETS,
  LABEL_PRESETS,
  QUOTE_LABEL_NAMES,
  QUOTE_LABEL_PRESETS,
  type QuoteLabelName,
} from "./label-presets";

type Labels = Partial<Record<LabelName, string>>;

/**
 * The language a document's labels are in: the one whose invoice or quote title matches. Labels
 * the user wrote themselves fall back to `fallback` (the app language).
 */
export function labelLanguage(labels: Labels, fallback: Locale): Locale {
  const title = labels.documentTitleLabel;
  const match = (Object.keys(LABEL_PRESETS) as Locale[]).find(
    (code) =>
      LABEL_PRESETS[code].documentTitleLabel === title ||
      QUOTE_LABEL_PRESETS[code].documentTitleLabel === title ||
      CREDIT_NOTE_LABEL_PRESETS[code].documentTitleLabel === title,
  );
  return match ?? fallback;
}

/** Whether a document carries the North American labels (US_LABELS), by its "Bill to:". */
export const isNorthAmerican = (labels: Labels) =>
  labels.recipientAddressLabel === US_LABELS.recipientAddressLabel;

/** Swaps the quote-specific labels; hidden (empty) labels stay hidden. */
function swap(labels: Labels, preset: Record<QuoteLabelName, string>) {
  return Object.fromEntries(
    QUOTE_LABEL_NAMES.map((name) => [name, labels[name] === "" ? "" : preset[name]]),
  ) as Record<QuoteLabelName, string>;
}

/** Labels for a quote made from an invoice-like document ("Invoice" -> "Quote", ...). */
export function toQuoteLabels(labels: Labels, fallback: Locale) {
  return swap(labels, QUOTE_LABEL_PRESETS[labelLanguage(labels, fallback)]);
}

/** Labels for the invoice an accepted quote becomes ("Quote" -> "Invoice", ...). */
export function toInvoiceLabels(labels: Labels, fallback: Locale) {
  return swap(labels, LABEL_PRESETS[labelLanguage(labels, fallback)]);
}

/** Labels for a credit note made from an invoice ("Invoice" -> "Credit note", ...). */
export function toCreditNoteLabels(labels: Labels, fallback: Locale) {
  const preset = CREDIT_NOTE_LABEL_PRESETS[labelLanguage(labels, fallback)];
  return Object.fromEntries(
    CREDIT_NOTE_LABEL_NAMES.map((name) => [name, labels[name] === "" ? "" : preset[name]]),
  ) as Record<(typeof CREDIT_NOTE_LABEL_NAMES)[number], string>;
}

/** The line naming the credited invoice, in the credit note's language. */
export function creditReference(labels: Labels, fallback: Locale, number: string, date: string) {
  return CREDIT_NOTE_LABEL_PRESETS[labelLanguage(labels, fallback)].reference
    .replace("{{number}}", number)
    .replace("{{date}}", date);
}
